import { create } from 'zustand';
import { authApi, type AuthUser } from '@/api/endpoints';
import { clearTokens, getTokens, restorePersistedSession, setTokens } from '@/api/tokens';
import { SUPPORTED_LOCALES, applyOrgKind, setLocale, type SupportedLocale } from '@/i18n';
import {
  getSnapshot,
  addUploadedListener,
  startQueue,
  stopQueue,
  subscribe,
} from '@/features/upload-queue';
// 直接从 store 进，不走 features/drafts 的桶文件：那里面的 useCaptureRecovery
// 反过来要用这个 store，绕一圈就成了循环依赖
import { clearAllDrafts } from '@/features/drafts/store';
import { canLogoutNow, isClockOutItem, shouldEndSessionAfterClockOut } from './session-policy';

/** 退出时清空缓存数据，由 App 注册；不这么做下一个登录的人会先看到上一个人的数据 */
let clearCachedData: (() => void) | null = null;
export function setCacheCleaner(fn: () => void): void {
  clearCachedData = fn;
}

/**
 * 退出原因，决定登录页显示哪句提示。
 *
 * - `manual`   自己点的退出
 * - `clockOut` 打了下班卡（CR-006 的主路径）
 * - `expired`  会话在服务端失效了 —— 通常是管理员强制下班，或者改了密码
 */
export type LogoutReason = 'manual' | 'clockOut' | 'expired';

/** 待退出标记：离线打的下班卡传上去之后才能退出，否则队列里的打卡永远传不出去 */
const PENDING_LOGOUT_KEY = 'construction.pendingClockOutLogout';

interface AuthState {
  user: AuthUser | null;
  /** 冷启动恢复会话期间为 true。路由守卫要等它结束，否则会先闪一下登录页 */
  restoring: boolean;
  lastLogoutReason: LogoutReason | null;
  login: (employeeNo: string, password: string) => Promise<AuthUser>;
  logout: (reason?: LogoutReason) => Promise<void>;
  restore: () => Promise<void>;
  /** 下班打卡进了离线队列：等队列传完再退出 */
  scheduleClockOutLogout: () => void;
  setUser: (user: AuthUser) => void;
  clearLogoutReason: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  restoring: true,
  lastLogoutReason: null,

  async login(employeeNo, password) {
    const result = await authApi.login(employeeNo, password);
    setTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    applyUserLocale(result.user);
    set({ user: result.user, lastLogoutReason: null, restoring: false });

    // 队列按用户隔离。本人上次没传完的东西，这时候会自动接着传。
    await startQueue(result.user.id);
    watchPendingLogout(result.user.id);

    return result.user;
  },

  /**
   * 冷启动恢复会话（CR-006）。
   *
   * 盘上只有 refresh token，access token 是空的 —— 第一个请求必然 401，
   * 由 api/client 的刷新逻辑换一套新的再重试。所以这里直接调 /auth/me 就行，
   * 不用为启动单独写一条换 token 的路径。
   */
  async restore() {
    if (!restorePersistedSession()) {
      set({ restoring: false });
      return;
    }
    try {
      const user = await authApi.me();
      applyUserLocale(user);
      set({ user, restoring: false });
      await startQueue(user.id);
      watchPendingLogout(user.id);
    } catch {
      // refresh token 被吊销了（管理员强制下班、改过密码），或者服务端连不上。
      // 两种情况都回登录页，但只有前者需要给提示 —— 分不清就都不提示，
      // 免得没网的时候吓唬人。
      clearTokens();
      set({ user: null, restoring: false });
    }
  },

  /**
   * 退出。先清本地再通知服务端：就算请求失败，这台设备上也已经登出了 ——
   * 共享手机的场景下，本地状态清干净比服务端吊销成功更要紧。
   */
  async logout(reason: LogoutReason = 'manual') {
    const tokens = getTokens();
    // 队列数据留在本地，不清 —— 等本人下次登录接着传
    stopQueue();
    // **草稿要清**（CR-037）。和队列相反：队列里的是系统答应过要传上去的，
    // 草稿只是他填到一半的东西，而这台手机马上会交到下一个人手里 ——
    // 留着等于把上一个人的采购金额和备注给下一个人看
    void clearAllDrafts();
    unwatchPendingLogout();
    clearTokens();
    clearPendingLogout();
    clearCachedData?.();
    set({ user: null, lastLogoutReason: reason, restoring: false });

    if (tokens?.refreshToken) {
      try {
        await authApi.logout(tokens.refreshToken);
      } catch {
        /* 服务端吊销失败不影响本地登出，token 也会自然过期 */
      }
    }
  },

  scheduleClockOutLogout() {
    const user = get().user;
    if (!user) return;
    try {
      localStorage.setItem(PENDING_LOGOUT_KEY, String(user.id));
    } catch {
      /* 存不下就退化成本次会话内有效，watch 仍然会在队列清空时退出 */
    }
    watchPendingLogout(user.id);
    maybeLogoutAfterQueue(user.id);
  },

  setUser(user) {
    set({ user });
  },

  clearLogoutReason() {
    set({ lastLogoutReason: null });
  },
}));

/**
 * 离线下班打卡的收尾。
 *
 * 打卡走离线队列，断网时点下班打卡只是入队。这时候立刻退出登录，
 * 队列里那条打卡就再也传不上去了 —— 传它需要凭证。
 * 所以只登记一个标记，等队列真的清空（传完或全部失败前）再退出。
 *
 * 传失败不退出：工人得留在登录态里重试，否则连补救的机会都没有。
 */
let unsubscribeQueue: (() => void) | null = null;
let unsubscribeUploaded: (() => void) | null = null;

function watchPendingLogout(userId: number): void {
  if (readPendingLogout() !== userId) return;
  unwatchPendingLogout();
  unsubscribeQueue = subscribe(() => maybeLogoutAfterQueue(userId));
  unsubscribeUploaded = addUploadedListener((item, result) => {
    if (!isClockOutItem(item)) return;
    const status = (result as { status?: string } | null)?.status;
    // 判定规则和边界见 session-policy.ts，那里有对应的用例
    if (!shouldEndSessionAfterClockOut(status)) {
      clearPendingLogout();
      unwatchPendingLogout();
      return;
    }
    maybeLogoutAfterQueue(userId);
  });
}

function unwatchPendingLogout(): void {
  unsubscribeQueue?.();
  unsubscribeQueue = null;
  unsubscribeUploaded?.();
  unsubscribeUploaded = null;
}

function maybeLogoutAfterQueue(userId: number): void {
  if (readPendingLogout() !== userId) return;
  if (!canLogoutNow(getSnapshot())) return;
  void useAuthStore.getState().logout('clockOut');
}

function readPendingLogout(): number | null {
  try {
    const raw = localStorage.getItem(PENDING_LOGOUT_KEY);
    // 账号 id 是整数（CR-019）。存进 localStorage 会变字符串，读回来要转一次，
    // 否则 `!== userId` 恒成立，待退出的标记永远等不到它的主人。
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
  } catch {
    return null;
  }
}

function clearPendingLogout(): void {
  try {
    localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch {
    /* 忽略 */
  }
}

/**
 * 服务端记着的语言优先，这样工人换设备也不用重新设一次。
 * 顺带按部署模式换「公司 / 分公司」的说法（CR-073）—— 都是「这个人该看到哪套字」，放在一起不会漏调
 */
function applyUserLocale(user: AuthUser): void {
  applyOrgKind(user.orgKind);
  if ((SUPPORTED_LOCALES as readonly string[]).includes(user.locale)) {
    setLocale(user.locale as SupportedLocale);
  }
}
